/**
 * Orka V3 — Task State map (Code Reference §3.3, PRD §6.4).
 *
 * Single source of truth for task state colours, labels, and intent mapping.
 * REMOVES duplicates: legacy `.status-*` utilities, ad-hoc status pills,
 * inline `statusColors` maps in TaskCard / BacklogPane / etc.
 *
 * Colour layer (Feb 2026): every colour is a CSS variable backed by
 * `design-system/tokens.css`. Light + dark mode are switched at the
 * :root / .dark selectors — NEVER hardcode state hex anywhere else.
 */
export const TASK_STATE_META = Object.freeze({
  open: {
    key: 'open',
    label: 'To Do',
    short: 'To Do',
    tone: 'neutral',
    intent: 'capture',
    color: 'var(--orka-state-open)',
    soft: 'var(--orka-state-open-soft)',
    text: 'var(--orka-state-open-text)',
    border: 'var(--orka-state-open-border)',
  },

  inProgress: {
    key: 'inProgress',
    label: 'In progress',
    short: 'Active',
    tone: 'info',
    intent: 'focus',
    color: 'var(--orka-state-in-progress)',
    soft: 'var(--orka-state-in-progress-soft)',
    text: 'var(--orka-state-in-progress-text)',
    border: 'var(--orka-state-in-progress-border)',
  },

  partial: {
    key: 'partial',
    label: 'Partial',
    short: 'Partial',
    tone: 'recover',
    intent: 'recover',
    color: 'var(--orka-state-partial)',
    soft: 'var(--orka-state-partial-soft)',
    text: 'var(--orka-state-partial-text)',
    border: 'var(--orka-state-partial-border)',
  },

  blocked: {
    key: 'blocked',
    label: 'Blocked',
    short: 'Blocked',
    tone: 'danger',
    intent: 'warn',
    color: 'var(--orka-state-blocked)',
    soft: 'var(--orka-state-blocked-soft)',
    text: 'var(--orka-state-blocked-text)',
    border: 'var(--orka-state-blocked-border)',
  },

  done: {
    key: 'done',
    label: 'Done',
    short: 'Done',
    tone: 'success',
    intent: 'commit',
    color: 'var(--orka-state-done)',
    soft: 'var(--orka-state-done-soft)',
    text: 'var(--orka-state-done-text)',
    border: 'var(--orka-state-done-border)',
  },

  archived: {
    key: 'archived',
    label: 'Archived',
    short: 'Archived',
    tone: 'muted',
    intent: 'system',
    color: 'var(--orka-state-archived)',
    soft: 'var(--orka-state-archived-soft)',
    text: 'var(--orka-state-archived-text)',
    border: 'var(--orka-state-archived-border)',
  },

  cancelled: {
    key: 'cancelled',
    label: 'Cancelled',
    short: 'Cancelled',
    tone: 'muted',
    intent: 'system',
    color: 'var(--orka-state-cancelled)',
    soft: 'var(--orka-state-cancelled-soft)',
    text: 'var(--orka-state-cancelled-text)',
    border: 'var(--orka-state-cancelled-border)',
  },

  deleted: {
    key: 'deleted',
    label: 'Deleted',
    short: 'Deleted',
    tone: 'muted',
    intent: 'system',
    color: 'var(--orka-state-deleted)',
    soft: 'var(--orka-state-deleted-soft)',
    text: 'var(--orka-state-deleted-text)',
    border: 'var(--orka-state-deleted-border)',
  },
});

/**
 * The status keys that should appear in user-facing pickers (planner status
 * change, kanban filters, etc). archived/cancelled/deleted are still valid
 * states, but only reachable through admin / batch flows.
 */
export const VISIBLE_STATE_KEYS = Object.freeze(['open', 'inProgress', 'partial', 'blocked', 'done']);

/** Canonical → backend legacy label (the API still expects 'To Do' etc). */
export const STATE_KEY_TO_LEGACY = Object.freeze({
  open: 'To Do',
  inProgress: 'In Progress',
  partial: 'Partial',
  blocked: 'Blocked',
  done: 'Done',
  archived: 'Archived',
  cancelled: 'Cancelled',
  deleted: 'Deleted',
});

/**
 * Adapter: legacy task_status strings → canonical key.
 * Lets old data ('To Do', 'In Progress', 'Done', etc.) migrate without backend changes.
 */
const LEGACY_STATE_MAP = {
  'To Do': 'open',
  'open': 'open',
  'Open': 'open',
  'In Progress': 'inProgress',
  'in_progress': 'inProgress',
  'Active': 'inProgress',
  'Partial': 'partial',
  'partial': 'partial',
  'Blocked': 'blocked',
  'blocked': 'blocked',
  'Done': 'done',
  'done': 'done',
  'Completed': 'done',
  'Cancelled': 'cancelled',
  'cancelled': 'cancelled',
  'Archived': 'archived',
  'archived': 'archived',
  'Deleted': 'deleted',
  'deleted': 'deleted',
};

export function getStateKey(value) {
  if (!value) return 'open';
  if (TASK_STATE_META[value]) return value;
  return LEGACY_STATE_MAP[value] || 'open';
}

export function getState(value) {
  return TASK_STATE_META[getStateKey(value)];
}

export const TASK_STATE_KEYS = Object.freeze(Object.keys(TASK_STATE_META));

/** CSS utility class (.orka-state-*) for the solid fill variant. Uses
 *  the kebab-case CSS class name (`.orka-state-in-progress`, not
 *  `.orka-state-inProgress`). */
const STATE_KEY_TO_CLASS = {
  open: 'open',
  inProgress: 'in-progress',
  partial: 'partial',
  blocked: 'blocked',
  done: 'done',
  archived: 'archived',
  cancelled: 'cancelled',
  deleted: 'deleted',
};

export function stateClass(value, { soft = false } = {}) {
  const classPart = STATE_KEY_TO_CLASS[getStateKey(value)] || 'open';
  return soft ? `orka-state-${classPart}-soft` : `orka-state-${classPart}`;
}
